// components/recurringDonationCalendar.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';
import ShowCurrencies from '../../utils/showCurrencies';
import CustomButton from '../../components/general/button';
import { useNavigate } from 'react-router-dom';
import { setDailyDonationStatus } from '../../utils/setDailyDonationStatus';

/**
 * Calendar logic:
 * 1) Find earliest donation date in donationSchedule.
 * 2) If earliest date <= today => start from today.
 * 3) Else if recurrence === "yearly" => start from earliestDonationLuxon.startOf('month').
 * 4) Else => start from today.
 * 5) If <20 days remain in that start month, also include next month.
 */
const RecurringDonationCalendar = ({
  donationSchedule,
  currency,
  timeZone,
  totalAmount,
  recurrence,               // 'daily' | 'weekly' | 'monthly' | 'yearly'
}) => {
  const navigate = useNavigate();

  // 1) If schedule is empty, show no calendar
  if (!donationSchedule || donationSchedule.length === 0) {
    return (
      <div className="w-full bg-[#DFEEF2] p-4 md:p-8 rounded-md">
        No schedule to display.
      </div>
    );
  }

  // 2) Build a map of donation days => amounts, find earliest
  const scheduleMap = {};
  let earliestDonationLuxon = null;

  donationSchedule.forEach((item) => {
    const dLuxon = DateTime.fromISO(item.date, { zone: timeZone }).startOf('day');
    const iso = dLuxon.toISODate();
    scheduleMap[iso] = item.amount;

    if (!earliestDonationLuxon || dLuxon < earliestDonationLuxon) {
      earliestDonationLuxon = dLuxon;
    }
  });

  // 3) Compare earliestDonationLuxon vs. "today"
  const today = DateTime.now().setZone(timeZone).startOf('day');
  let calendarStart;

  if (earliestDonationLuxon <= today) {
    // Earliest donation is today or in the past => show from today
    calendarStart = today;
  } else {
    // earliest donation is in the future
    if (recurrence === 'yearly') {
      // For Yearly in a future month => show entire month
      calendarStart = earliestDonationLuxon.startOf('month');
    } else {
      // For weekly/monthly/daily => show from "today"
      calendarStart = today;
    }
  }

  // 4) If <20 days remain in that start month, also show next month
  const endOfStartMonth = calendarStart.endOf('month');
  const daysLeft = Math.floor(endOfStartMonth.diff(calendarStart, 'days').days) + 1;

  let finalEnd = endOfStartMonth;
  if (daysLeft < 20) {
    finalEnd = endOfStartMonth.plus({ months: 1 }).endOf('month');
  }

  // 5) Build an array of days from calendarStart -> finalEnd
  const allDays = [];
  let cursor = calendarStart;
  while (cursor <= finalEnd) {
    allDays.push(cursor);
    cursor = cursor.plus({ days: 1 });
  }

  // 6) Group them by Month-Year
  const groupedByMonth = {};
  allDays.forEach((luxDay) => {
    const monthLabel = luxDay.toFormat('LLLL yyyy');
    if (!groupedByMonth[monthLabel]) {
      groupedByMonth[monthLabel] = [];
    }
    groupedByMonth[monthLabel].push(luxDay);
  });

  // Minimum donation check
  const MINIMUM_DONATION = currency.toLowerCase() === 'myr' ? 200 : 50;
  const isMinimumMet = totalAmount >= MINIMUM_DONATION;

  return (
    <div className="w-full bg-[#DFEEF2] p-4 md:p-8 rounded-md">
      <h2 className="text-[#253B7E] text-lg md:text-2xl mb-4 font-bold">
        Your Donation Calendar
      </h2>

      {Object.keys(groupedByMonth).map((monthLabel) => {
        const daysInThisMonth = groupedByMonth[monthLabel];

        return (
          <div key={monthLabel} className="mb-6">
            <h3 className="text-black text-md md:text-xl mb-3">{monthLabel}</h3>
            <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-9 gap-2 md:gap-2">
              {daysInThisMonth.map((luxDay) => {
                const iso = luxDay.toISODate();
                const amount = scheduleMap[iso] || 0;
                const highlight = amount > 0 ? 'bg-yellow-100 border-yellow-500' : '';

                return (
                  <div
                    key={iso}
                    className={`border border-stone-800 p-1 md:p-2 rounded text-center ${highlight}`}
                  >
                    {/* Show date info */}
                    <p className="text-xs text-[#797979]">
                      {luxDay.toFormat('LLLL d, yyyy')}
                    </p>
                    <p className="text-sm text-[#253B7E] my-1">
                      {luxDay.toFormat('cccc')}
                    </p>
                    <div className="w-full rounded bg-[#253B7E] text-white text-center md:py-1">
                      <div className="text-base md:text-lg font-semibold">
                        {ShowCurrencies(currency, amount)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {Object.keys(groupedByMonth).length > 0 && (
        <div className="mt-3">
          <CustomButton
            type="button"
            title="Schedule your donation"
            className="w-full sm:w-auto"
            onClick={() => {
              if (isMinimumMet) {
                setDailyDonationStatus();
                // For example, store the recurrence type so you know what they selected
                localStorage.setItem('RECURRING_DONATION_TYPE', recurrence);
                navigate('/checkout', { state: { from: 'recurring-giving' } });
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

RecurringDonationCalendar.propTypes = {
  donationSchedule: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
    })
  ).isRequired,
  currency: PropTypes.string.isRequired,
  timeZone: PropTypes.string.isRequired,
  totalAmount: PropTypes.number.isRequired,
  recurrence: PropTypes.string.isRequired, // 'daily'|'weekly'|'monthly'|'yearly'
  isLastTenNightsGiving: PropTypes.bool,
};

export default RecurringDonationCalendar;
